// Pure date + layout math for the Calendar page's four views. No dependency:
// the app ships no date library, and every operation here is either local-time
// arithmetic or Intl formatting, both of which the platform already does.
//
// Everything is local-time on purpose. A calendar's "day" is the viewer's day,
// so all bucketing keys off the local Y-M-D rather than the ISO/UTC one --
// an event at 00:30 CEST must not land on the previous day's cell.

export const VIEWS = ["month", "week", "day3", "day"];

/** Days shown per view (month is handled by monthGrid, not this map). */
export const VIEW_DAYS = { day: 1, day3: 3, week: 7 };

export const MINUTES_PER_DAY = 1440;

export function startOfDay(d) {
  return new Date(d.getFullYear(), d.getMonth(), d.getDate());
}

export function addDays(d, n) {
  // Via the day component rather than +n*86400e3: DST transitions make some
  // local days 23 or 25 hours long, and "the next day at midnight" is what
  // every caller here actually wants.
  return new Date(d.getFullYear(), d.getMonth(), d.getDate() + n);
}

/** Same day-of-month in another month, clamped (31 Jan + 1 month = 28/29 Feb,
 *  not 2/3 March). The day is preserved so switching month -> week after
 *  paging lands on the week the user was looking at, not on the 1st. */
export function addMonths(d, n) {
  const target = new Date(d.getFullYear(), d.getMonth() + n, 1);
  const lastDay = new Date(target.getFullYear(), target.getMonth() + 1, 0).getDate();
  return new Date(target.getFullYear(), target.getMonth(), Math.min(d.getDate(), lastDay));
}

/** Monday-first week start, matching the existing month grid's convention. */
export function startOfWeek(d) {
  const day = startOfDay(d);
  return addDays(day, -((day.getDay() + 6) % 7));
}

export function isSameDay(a, b) {
  return (
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate()
  );
}

/** Stable local-date key for bucketing. Not toDateString(): that one is
 *  locale-independent but parses back ambiguously and sorts alphabetically. */
export function dayKey(d) {
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${d.getFullYear()}-${m}-${day}`;
}

export function minutesOfDay(d) {
  return d.getHours() * 60 + d.getMinutes();
}

/** The visible day list for a view. Month always returns 6 weeks (42 days)
 *  so the grid's height never jumps between a 5- and 6-week month -- the
 *  whole page would reflow on every next-month click otherwise. */
export function visibleDays(view, cursor) {
  if (view === "month") {
    const first = new Date(cursor.getFullYear(), cursor.getMonth(), 1);
    const gridStart = startOfWeek(first);
    return Array.from({ length: 42 }, (_, i) => addDays(gridStart, i));
  }
  const count = VIEW_DAYS[view] || 7;
  // Week snaps to the Monday; day/3-day start at the cursor itself, so
  // "next" walks by 1 or 3 days rather than jumping to a week boundary.
  const start = view === "week" ? startOfWeek(cursor) : startOfDay(cursor);
  return Array.from({ length: count }, (_, i) => addDays(start, i));
}

/** How far one prev/next press moves the cursor, per view. */
export function shiftCursor(view, cursor, direction) {
  if (view === "month") return addMonths(cursor, direction);
  if (view === "week") return addDays(startOfWeek(cursor), 7 * direction);
  return addDays(cursor, (VIEW_DAYS[view] || 1) * direction);
}

/** Heading label for the current range: "August 2026", "11 – 17 Aug 2026",
 *  "Thu 14 Aug 2026". Built from Intl so HU gets its own ordering. */
export function rangeLabel(view, cursor, locale) {
  const days = visibleDays(view, cursor);
  if (view === "month") {
    return cursor.toLocaleDateString(locale, { month: "long", year: "numeric" });
  }
  if (view === "day") {
    return days[0].toLocaleDateString(locale, {
      weekday: "short", day: "numeric", month: "short", year: "numeric",
    });
  }
  const first = days[0];
  const last = days[days.length - 1];
  const sameMonth = first.getMonth() === last.getMonth() && first.getFullYear() === last.getFullYear();
  const from = first.toLocaleDateString(locale, sameMonth ? { day: "numeric" } : { day: "numeric", month: "short" });
  const to = last.toLocaleDateString(locale, { day: "numeric", month: "short", year: "numeric" });
  return `${from} – ${to}`;
}

export function weekdayLabels(locale, short = true) {
  // 2024-01-01 was a Monday, so this walks Mon..Sun without hardcoding names.
  return Array.from({ length: 7 }, (_, i) =>
    new Date(2024, 0, 1 + i).toLocaleDateString(locale, { weekday: short ? "short" : "long" })
  );
}

/** Full time, day period included: "09:00 AM" / "09:00". For the popover and
 *  title tooltips, where there's room and no position to disambiguate. */
export function formatTime(d, locale) {
  return d.toLocaleTimeString(locale, { hour: "2-digit", minute: "2-digit" });
}

/** "9:00 AM" / "9:00" -- no leading zero, day period kept. Month chips need
 *  the AM/PM: a month cell has no vertical axis to imply it. */
export function formatTimeShort(d, locale) {
  return d.toLocaleTimeString(locale, { hour: "numeric", minute: "2-digit" });
}

/**
 * "9:00" / "14:00" -- the hour and minute only, in the locale's own numbering
 * and clock. Used inside time-grid blocks, where a 3-lane column leaves ~45px
 * and where the block's vertical position already says which half of the day
 * it is, so the day period is redundant width.
 *
 * Assembled from formatToParts rather than stripping "AM" with a regex, which
 * would only work for the locales whose day period happens to look English.
 */
export function formatTimeCompact(d, locale) {
  const parts = new Intl.DateTimeFormat(locale, { hour: "numeric", minute: "2-digit" }).formatToParts(d);
  const hour = parts.find((p) => p.type === "hour")?.value ?? String(d.getHours());
  const minute = parts.find((p) => p.type === "minute")?.value ?? String(d.getMinutes()).padStart(2, "0");
  return `${hour}:${minute}`;
}

/** Hour-gutter labels. Uses a fixed date so only the hour varies. */
export function hourLabel(hour, locale) {
  return new Date(2024, 0, 1, hour).toLocaleTimeString(locale, { hour: "numeric" });
}

/**
 * Side-by-side layout for events that overlap in time.
 *
 * Events are grouped into clusters (a run with no gap), and within a cluster
 * each event takes the leftmost column whose last event has already ended.
 * The cluster's column count is what divides the width, not the whole day's --
 * two overlapping events at 09:00 must not squeeze the unrelated 17:00 one.
 *
 * `events` must already be filtered to one day and carry {start, end} Dates.
 * Returns copies with {lane, lanes} added and `start`/`end` left as the Dates
 * they came in as -- the same objects are handed to the popover, which formats
 * them, so timestamps must not leak out of here.
 */
export function layoutOverlaps(events) {
  // Comparison happens on milliseconds (Date + number would concatenate into a
  // string), while the Date objects themselves ride along untouched.
  const items = events
    .map((event) => ({
      event,
      from: +event.start,
      // A visual minimum, so a zero-length event still collides with its
      // neighbour instead of stacking invisibly on top of it.
      to: Math.max(+event.end, +event.start + 15 * 60000),
    }))
    .sort((a, b) => a.from - b.from || b.to - a.to || String(a.event.id).localeCompare(String(b.event.id)));

  const out = [];
  let cluster = [];
  let clusterEnd = -Infinity;

  const flush = () => {
    if (!cluster.length) return;
    const lanes = [];
    for (const item of cluster) {
      let lane = lanes.findIndex((endsAt) => endsAt <= item.from);
      if (lane === -1) { lane = lanes.length; lanes.push(0); }
      lanes[lane] = item.to;
      item.lane = lane;
    }
    for (const item of cluster) {
      out.push({ ...item.event, lane: item.lane, lanes: lanes.length });
    }
    cluster = [];
    clusterEnd = -Infinity;
  };

  for (const item of items) {
    if (cluster.length && item.from >= clusterEnd) flush();
    cluster.push(item);
    clusterEnd = Math.max(clusterEnd, item.to);
  }
  flush();
  return out;
}

/** Snap a fraction (0..1) of the day to the nearest `step`-minute slot. */
export function snapMinutes(fraction, step = 30) {
  const clamped = Math.min(Math.max(fraction, 0), 1);
  const raw = clamped * MINUTES_PER_DAY;
  const snapped = Math.round(raw / step) * step;
  return Math.min(snapped, MINUTES_PER_DAY - step);
}

/** Local `YYYY-MM-DDTHH:mm`, the only shape <input type="datetime-local">
 *  accepts. Deliberately not toISOString(), which renders the UTC wall-clock
 *  time and would show a European user 07:30 for their own 09:30 meeting.
 *  Going the other way, values are sent to the API as ISO (`new Date(input)
 *  .toISOString()`) -- the columns are `timestamptz`, and that's what
 *  MeetingDetail.jsx already does. */
export function toLocalInput(value) {
  if (!value) return "";
  const d = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(d.getTime())) return "";
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

/** Local `YYYY-MM-DD` for <input type="date">, same reasoning as above. */
export function toDateInput(value) {
  if (!value) return "";
  const d = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(d.getTime())) return "";
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

export function dateAtMinutes(day, minutes) {
  return new Date(day.getFullYear(), day.getMonth(), day.getDate(), 0, minutes);
}
